// TelcoTrack — detekce kódování českých textů (UTF-8 s BOM nebo Windows-1250).
// Použití: CSV exporty, DXF layer names z CAD (ANSI_1250 vs UTF-8).

const CP1250 = 'windows-1250';
const UTF8 = 'utf-8';

/**
 * Typické znaky vzniklé při dekódování UTF-8 jako Windows-1250 (české diakritiky).
 * Např. Č→ÄŚ, č→ÄŤ, Í→ĂŤ, í→Ă­, Ř→Ĺ™, š→Ĺˇ.
 */
const UTF8_MOJIBAKE_PATTERN =
  /ď»ż|Ĺ™|Ĺˇ|Ĺľ|Ĺ˝|ĹĽ|ÄŚ|ÄŤ|ÄŽ|ÄŹ|Ä›|Ăˇ|Ă­|ĂŤ|Ăł|Ăş|Ă˝|Ă©|Ă¨|PoÄŤet|SouĹ™adnice|ATRAKÄŚ/;

/**
 * Dekóduje textový buffer: UTF-8 (BOM / validní multi-byte / mojibake heuristika)
 * nebo Windows-1250. CAD DXF z CZ často ANSI_1250; moderní exporty UTF-8.
 */
export function decodeCzechTextBuffer(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  if (bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    return new TextDecoder(UTF8).decode(buffer);
  }

  // Prefer UTF-8 when the file is valid UTF-8 with multi-byte sequences
  // (ASCII-only is valid as both; CP1250 Czech bytes are usually invalid UTF-8).
  if (isValidUtf8(bytes) && hasMultibyteUtf8(bytes)) {
    return new TextDecoder(UTF8).decode(buffer);
  }

  const asCp1250 = new TextDecoder(CP1250).decode(buffer);
  if (looksLikeUtf8MisreadAsCp1250(asCp1250) && isValidUtf8(bytes)) {
    return new TextDecoder(UTF8).decode(buffer);
  }
  return asCp1250;
}

/** @deprecated Prefer decodeCzechTextBuffer — kept for CSV call sites. */
export function decodeCsvBuffer(buffer: ArrayBuffer): string {
  return decodeCzechTextBuffer(buffer);
}

export function looksLikeUtf8MisreadAsCp1250(text: string): boolean {
  // Layer names / CAD tables may sit past the first few KB of a DXF.
  const sample = text.length <= 65536 ? text : `${text.slice(0, 32768)}${text.slice(-32768)}`;
  return UTF8_MOJIBAKE_PATTERN.test(sample);
}

function hasMultibyteUtf8(bytes: Uint8Array): boolean {
  for (let i = 0; i < bytes.length; i++) {
    if (bytes[i] >= 0x80) return true;
  }
  return false;
}

/** Strict UTF-8 structural check (no overlong / surrogates / truncated sequences). */
function isValidUtf8(bytes: Uint8Array): boolean {
  let i = 0;
  while (i < bytes.length) {
    const b = bytes[i];
    if (b <= 0x7f) {
      i += 1;
      continue;
    }
    let need = 0;
    let minCode = 0;
    if (b >= 0xc2 && b <= 0xdf) {
      need = 1;
      minCode = 0x80;
    } else if (b >= 0xe0 && b <= 0xef) {
      need = 2;
      minCode = 0x800;
    } else if (b >= 0xf0 && b <= 0xf4) {
      need = 3;
      minCode = 0x10000;
    } else {
      return false;
    }
    if (i + need >= bytes.length) return false;
    let code = b & (need === 1 ? 0x1f : need === 2 ? 0x0f : 0x07);
    for (let j = 1; j <= need; j++) {
      const c = bytes[i + j];
      if (c < 0x80 || c > 0xbf) return false;
      code = (code << 6) | (c & 0x3f);
    }
    if (code < minCode) return false;
    if (code >= 0xd800 && code <= 0xdfff) return false;
    if (code > 0x10ffff) return false;
    // Overlong / out-of-range lead for 3- and 4-byte forms
    if (b === 0xe0 && bytes[i + 1] < 0xa0) return false;
    if (b === 0xed && bytes[i + 1] >= 0xa0) return false;
    if (b === 0xf0 && bytes[i + 1] < 0x90) return false;
    if (b === 0xf4 && bytes[i + 1] > 0x8f) return false;
    i += need + 1;
  }
  return true;
}
